import IonIcon from '@reacticons/ionicons'
import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import { CheckBox } from 'src/components/global/CheckBox/CheckBox'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'
import { FilterOptionItem, TaxonomyType } from 'src/types'
import { getTaxonomyQueryFieldName } from 'src/utils/getTaxonomyQueryFieldName'

interface TopicListProps {
  title: TaxonomyType
  items: FilterOptionItem[]
  opened: boolean
  disabled?: boolean
  showCheckbox?: boolean
  onSelectAccordion: (title: TaxonomyType) => void
  onClickWhenDisabled?: () => void
}
export const TopicList: React.FC<TopicListProps> = ({
  title,
  items,
  opened,
  disabled = false,
  showCheckbox = false,
  onSelectAccordion,
  onClickWhenDisabled,
}) => {
  const [data, setData] = useState<FilterOptionItem[]>(items)
  const router = useRouter()
  const { updateQueryParams } = useUpdateQueryParams()
  const contentSpace = useRef(null)
  const param = getTaxonomyQueryFieldName(title)

  const updateRouter = (data: FilterOptionItem[]) => {
    if (!param) return

    let selection: string[] = []
    data.map((item) => {
      item.children?.map((subItem) => {
        if (subItem.selected) {
          const _id = parseInt(item.id) * 10000 + parseInt(subItem.id.split(',').join('-'))
          selection.push(_id.toString())
        }
      })
    })
    selection = [...new Set(selection)]

    if (selection.length === 0) {
      updateQueryParams({ [param]: undefined, page: undefined })
    } else {
      updateQueryParams({ [param]: selection.join(','), page: undefined })
    }
  }
  const getFromRouter = () => {
    if (!param) return items
    let refinedItems: FilterOptionItem[] = items
    if (router.query[param]) {
      const paths = (router.query[param] as string).split('_')
      const checks = paths[0].split(',').map((check) => parseInt(check))
      const checkTopicIds: Set<string> = new Set<string>()
      checks.forEach((check) => {
        const topicId = Math.floor(check / 10000).toString()
        checkTopicIds.add(topicId)
      })
      refinedItems = items.map((item) => {
        item.indeterminate = checkTopicIds.has(item.id.toString())
        let isAllChildrenSelected = true
        item.children?.forEach((subItem) => {
          //const childrenPathId = (parseInt(item.id) * 10000 + parseInt(subItem.id)).toString()
          const childrenPathId = parseInt(item.id) * 10000 + parseInt(subItem.id)
          if (checks.includes(childrenPathId)) {
            subItem.selected = true
            subItem.indeterminate = true
          } else {
            subItem.selected = false
            subItem.indeterminate = false
            isAllChildrenSelected = false
          }
        })
        item.allChildrenSelected = isAllChildrenSelected
        item.selected = isAllChildrenSelected
        return item
      })
    } else {
      refinedItems = items.map((item) => {
        item.selected = false
        item.allChildrenSelected = false
        item.children?.forEach((subItem) => {
          subItem.selected = false
        })
        return item
      })
    }
    return refinedItems
  }

  useEffect(() => {
    setData(getFromRouter())
  }, [router, opened])

  const toggleAccordion = () => {
    if (disabled) {
      onClickWhenDisabled && onClickWhenDisabled()
      return
    }
    onSelectAccordion(title)
  }

  const toggleParent = (topicId: string) => {
    if (disabled) {
      onClickWhenDisabled && onClickWhenDisabled()
      return
    }
    setData(
      data.map((item) => {
        if (item.id !== topicId) {
          item.opened = false
        } else {
          item.opened = !item.opened
        }
        return item
      }),
    )
  }

  const onParentSelect = (selected: boolean, prop?: string) => {
    if (disabled) {
      onClickWhenDisabled && onClickWhenDisabled()
      return
    }
    const idx = items.findIndex((item) => item.id === prop)
    if (idx < 0) return

    const newData = [...data]
    newData[idx].selected = selected
    newData[idx].indeterminate = selected
    newData[idx].children?.forEach((subItem) => {
      subItem.selected = selected
      subItem.indeterminate = selected
    })
    setData(newData)
    updateRouter(newData)
  }

  const onChildSelected = (parentId: string, selected: boolean, childId?: string) => {
    if (disabled) {
      onClickWhenDisabled && onClickWhenDisabled()
      return
    }
    const newData = [...data]
    newData.forEach((item) => {
      if (item.id !== parentId) return
      if (!item.children) return

      const idx = item.children.findIndex((subItem) => subItem.id === childId)
      if (idx < 0) return

      items.some((i) => (i.opened = i.id === childId))
      item.children[idx].selected = selected
      item.children[idx].indeterminate = selected
      item.indeterminate = item.children.some((subItem) => subItem.selected)
      item.selected = item.children.every((subItem) => subItem.selected)
      item.opened = true
    })

    setData(newData)
    updateRouter(newData)
  }

  const sortItems = (filterItems: FilterOptionItem[]) => {
    return filterItems.sort((a, b) => a.name.localeCompare(b.name))
  }

  return (
    <div className="w-full flex flex-col">
      <button
        className="py-3 box-border appearance-none cursor-pointer focus:outline-none flex items-center justify-between"
        onClick={toggleAccordion}
      >
        <p className="inline-block text-footnote light  font-bold font-primary text-textdark uppercase text-[0.94rem]">
          {title}
        </p>
        {opened ? <IonIcon name="chevron-up-outline" /> : <IonIcon name="chevron-down-outline" />}
      </button>
      <div
        ref={contentSpace}
        style={{ maxHeight: opened ? `1500px` : '0px' }}
        className="transition-max-height duration-500 overflow-hidden overflow-y-scroll ease-in-out"
      >
        {data.map((item, index) => {
          let clsName = ''
          if (index === 0) {
            clsName = 'pl-[1.25rem] font-bold font-primary text-textdark text-[0.94rem] mt-[12px] '
          } else if (index === data.length - 1) {
            clsName = 'pl-[1.25rem] font-bold font-primary text-textdark text-[0.94rem] mb-[12px]'
          } else {
            clsName = 'pl-[1.25rem] font-bold font-primary text-textdark text-[0.94rem]'
          }

          return (
            <div key={index} className={clsName}>
              <div className="flex flex-row w-full pb-4">
                <div className="flex items-start w-[15px]">
                  <CheckBox
                    label=""
                    value={item.id}
                    onChanged={onParentSelect}
                    selected={!!item.selected}
                    indeterminate={!!item.indeterminate}
                  />
                </div>
                <button
                  className="box-border appearance-none cursor-pointer focus:outline-none flex flex-row items-center pl-0 w-full"
                  onClick={() => toggleParent(item.id)}
                >
                  <p className="inline-block font-primary font-medium text-[15px] text-darkgray flex-shrink-0 pl-[0.5rem] text-left max-w-[160px]">
                    {item.name}
                  </p>
                  {item.opened ? (
                    <div className="flex items-center w-[15px] ml-auto">
                      <IonIcon name="chevron-up-outline" />
                    </div>
                  ) : (
                    <div className="flex items-center w-[15px] ml-auto">
                      <IonIcon name="chevron-down-outline" />
                    </div>
                  )}
                </button>
              </div>
              {item.opened &&
                sortItems(item.children || []).map((subItem, idx) => {
                  let subClsName = ''
                  if (idx === 0) {
                    subClsName = 'pl-[1.25rem] font-bold font-primary text-textdark text-[0.94rem] mt-[-4px] mb-[10px]'
                  } else if (idx === (item.children?.length || 0) - 1) {
                    subClsName = 'pl-[1.25rem] font-bold font-primary text-textdark text-[0.94rem] mb-[10px]'
                  } else {
                    subClsName = 'pl-[1.25rem] font-bold font-primary text-textdark text-[0.94rem] mb-[10px]'
                  }
                  return (
                    <div key={idx} className={subClsName}>
                      {showCheckbox && (
                        <CheckBox
                          label={subItem.name}
                          value={subItem.id}
                          onChanged={(selected: boolean) => onChildSelected(item.id, selected, subItem.id)}
                          selected={!!subItem.selected}
                          indeterminate={!!subItem.indeterminate}
                        />
                      )}
                    </div>
                  )
                })}
            </div>
          )
        })}
      </div>
    </div>
  )
}
